import React, { useRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMapGL from 'react-map-gl';
import get from 'lodash/get';

import { MapControl, EditMapPopup } from 'components';
import addCustomAttribution from 'components/shared/Map/mapAttribution';
import { zoomToDatapoints } from 'components/shared/Map/zoomToPoints';
import { accessToken } from 'utils/constants';

import EditDatasetsContextMenu from './EditDatasetsContextMenu/container';
import { StyledEditMap } from './style';


const useZoomHandling = (mapDatapoints, changeMapState) => {
  useEffect(() => {
    // if we remove/create/update datapoints we need to change zoom
    zoomToDatapoints(mapDatapoints, changeMapState, 'datasets');
    // eslint-disable-next-line
  }, []);
};

const useCustomMapAttribution = (mapRef) => {
  useEffect(() => {
    // add information icon on the map
    const mapInstance = get(mapRef, 'current', false);
    if (mapInstance) {
      addCustomAttribution(mapInstance);
    }
    // eslint-disable-next-line
  }, []);
};

const usePopupDestroying = ({ selectPointEditMap, closeContextMenu }) => {
  useEffect(() => () => {
    selectPointEditMap({});
    closeContextMenu();
    // eslint-disable-next-line
  }, []);
};


const EditMapDatasets = ({
  mapDatapoints,
  mapViewport,
  changeMapViewport,
  onViewportChange,
  showMapPopup,
  selectPointEditMap,
  closeContextMenu,
  handleContextMenu,
}) => {
  const [isHoveringPin, setIsHoveringPin] = useState(false);
  const mapRef = useRef();
  useCustomMapAttribution(mapRef);

  useZoomHandling(mapDatapoints, changeMapViewport);

  usePopupDestroying({ selectPointEditMap, closeContextMenu });

  const { viewport } = mapViewport;
  const interactiveLayerIds = useMemo(() => {
    const rawLayers = typeof mapDatapoints.get === 'function'
      ? mapDatapoints.get('layers', null)
      : get(mapDatapoints, 'layers', []);
    const layers = rawLayers && typeof rawLayers.toJS === 'function' ? rawLayers.toJS() : (rawLayers || []);

    return layers
      .map(layer => get(layer, 'id'))
      .filter(layerId => (
        typeof layerId === 'string'
        && (layerId.startsWith('point') || layerId.startsWith('clusters') || layerId.startsWith('cluster-count'))
      ));
  }, [mapDatapoints]);

  const handleMapHover = (event) => {
    const hoveredPin = get(event, 'features', []).length > 0;

    if (hoveredPin !== isHoveringPin) {
      setIsHoveringPin(hoveredPin);
    }
  };

  return (
    <StyledEditMap>
      <ReactMapGL
        width="100%"
        height="100%"
        ref={mapRef}
        mapStyle={mapDatapoints}
        attributionControl={false}
        mapboxApiAccessToken={accessToken}
        onViewportChange={onViewportChange}
        onHover={handleMapHover}
        onContextMenu={handleContextMenu}
        onClick={showMapPopup}
        interactiveLayerIds={interactiveLayerIds}
        getCursor={() => (isHoveringPin ? 'pointer' : 'default')}
        {...viewport}
      >
        <MapControl />
        <EditDatasetsContextMenu />
        <EditMapPopup showEditButtons />
      </ReactMapGL>
    </StyledEditMap>
  );
};

EditMapDatasets.propTypes = {
  mapViewport: PropTypes.object.isRequired,
  mapDatapoints: PropTypes.object.isRequired,
  changeMapViewport: PropTypes.func.isRequired,
  onViewportChange: PropTypes.func.isRequired,
  selectPointEditMap: PropTypes.func.isRequired,
  showMapPopup: PropTypes.func.isRequired,
};

export default EditMapDatasets;
